import { queryOptions, useQuery } from '@tanstack/react-query';

import { K8sProvider } from '@cast/shared-types';
import { ComponentsAPIInstallOperatorProviderTypeEnum } from '@cast/types-cluster-management';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';
import { RosaClusterOnboardingParameters } from 'types/cluster';

type GetAgentInstallScriptParameters = NonNullable<
  Parameters<typeof apiClient.autoscaler.getAgentInstallScript>[0]
>;

const getProviderType = (
  provider: GetAgentInstallScriptParameters['provider']
): ComponentsAPIInstallOperatorProviderTypeEnum => {
  switch (provider) {
    case 'aks':
      return ComponentsAPIInstallOperatorProviderTypeEnum.AKS;
    case 'eks':
      return ComponentsAPIInstallOperatorProviderTypeEnum.EKS;
    case 'gke':
      return ComponentsAPIInstallOperatorProviderTypeEnum.GKE;
    default:
      return ComponentsAPIInstallOperatorProviderTypeEnum.PROVIDER_TYPE_UNSPECIFIED;
  }
};

const EXTERNAL_CLUSTER_REGISTRATION_SCRIPT_QUERY_KEY =
  'external-cluster-registration-script';

const getAgentScriptQueryKey = (params: {
  provider?: `${K8sProvider}`;
  rosa?: RosaClusterOnboardingParameters;
  anywhere?: { clusterName?: string };
  onboardOperator?: boolean;
}) =>
  [
    EXTERNAL_CLUSTER_REGISTRATION_SCRIPT_QUERY_KEY,
    params.provider,
    JSON.stringify(params.rosa || {}),
    JSON.stringify(params.anywhere || {}),
    params.onboardOperator ?? false,
  ] as const;

type AgentScriptParams = {
  provider?: `${K8sProvider}`;
  rosa?: RosaClusterOnboardingParameters;
  anywhere?: { clusterName?: string };
  onboardOperator?: boolean;
  enabled?: boolean;
};

const getAgentScriptQueryOptions = ({
  onboardOperator,
  provider,
  rosa,
  anywhere,
  enabled = true,
}: AgentScriptParams) =>
  queryOptions({
    queryKey: getAgentScriptQueryKey({
      provider,
      rosa,
      anywhere,
      onboardOperator,
    }),
    queryFn: async () => {
      const providerTyped =
        provider as GetAgentInstallScriptParameters['provider']; // TODO(Vilius): temp fix until autoscaler properly supports self-hosted providers
      const operatorProvider = getProviderType(providerTyped);
      if (
        onboardOperator &&
        operatorProvider !==
          ComponentsAPIInstallOperatorProviderTypeEnum.PROVIDER_TYPE_UNSPECIFIED
      ) {
        const { data } =
          await apiClient.clusterManagement.getComponentsInstallOperator({
            providerType: operatorProvider,
          });
        return data.script;
      } else {
        const { data } = await apiClient.autoscaler.getAgentInstallScript({
          provider: providerTyped,
          openshiftRunAsUser: rosa?.runAsUser,
          openshiftRunAsGroup: rosa?.runAsGroup,
          openshiftFsGroup: rosa?.fsGroup,
          anywhereClusterName: anywhere?.clusterName,
        });
        return data;
      }
    },
    enabled: makeIsEnabled(
      !!provider,
      !anywhere || !!anywhere.clusterName,
      enabled
    ),
  });

export const useAgentScriptQuery = ({
  onboardOperator,
  provider,
  rosa,
  anywhere,
  enabled = true,
}: AgentScriptParams) => {
  const { isLoading, isFetching, data, error, refetch } = useQuery(
    getAgentScriptQueryOptions({
      onboardOperator,
      provider,
      rosa,
      anywhere,
      enabled,
    })
  );

  return {
    isLoading,
    isFetching,
    script: data as string | undefined,
    error,
    refetch,
  };
};
